@if (showSharePopup) {
  <app-popup
    [heading]="'Share File: ' + shareFile.blob_name"
    [confirmText]="'Save'"
    [cancelText]="'Cancel'"
    [isConfirmDisabled]="isShareButtonDisabled"
    (confirmClicked)="onConfirmShare()"
    (cancelClicked)="onSharePopupClose()"
  >
    <div class="share-form">
      <!-- Add New Recipients Section -->
      <div class="add-recipients-section">
        <h4 class="section-title">Add New Recipients</h4>
        <div class="form-row to-field-row">
          <div
            class="to-field-container"
            (appClickOutside)="isUsersDropdownOpen = false"
          >
            <div class="to-field-header">
              <label class="to-label" for="user-search">Recipients:</label>
              <input
                type="text"
                id="user-search"
                class="search-input"
                placeholder="Search users..."
                [(ngModel)]="searchText"
                (ngModelChange)="onSearchUser($event)"
                (focus)="onInputFocus()"
              />
              @if (isUsersDropdownOpen) {
                <button
                  type="button"
                  class="dropdown-toggle"
                  (click)="onCloseDropdown()"
                  title="Close dropdown"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 384 512"
                    width="16px"
                    height="16px"
                    fill="currentColor"
                  >
                    <path
                      d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z"
                    />
                  </svg>
                </button>
              }
            </div>

            <!-- Selected Tags Section -->
            @if (getSelectedUsers().length > 0) {
              <div class="selected-tags-section">
                <div class="tags-wrapper">
                  @for (user of getSelectedUsers(); track user) {
                    <div class="tag-item">
                      <span class="tag-text">{{ user }}</span>
                      <button
                        type="button"
                        class="remove-tag-btn"
                        (click)="removeUser(user)"
                        title="Remove {{ user }}"
                      >
                        <svg
                          width="12px"
                          height="12px"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 384 512"
                        >
                          <path
                            d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z"
                          />
                        </svg>
                      </button>
                    </div>
                  }
                </div>
              </div>
            }

            @if (shareErrors.recipients) {
              <div class="error-message">{{ shareErrors.recipients }}</div>
            }

            <!-- Users dropdown positioned within the to-field-container -->
            @if (isUsersDropdownOpen) {
              <div class="users-list-dropdown">
                <div class="users-list">
                  <div class="users-data-list-wrapper">
                    @if (!isLoadingUsers) {
                      @for (user of userSuggestions; track user.id) {
                        <div class="checkbox-section">
                          <label class="checkbox-container">
                            <input
                              type="checkbox"
                              [checked]="selectedUsersToShare.includes(user.id)"
                              (change)="
                                onUsersSelection($event, {
                                  username: user.username,
                                  name: user.name,
                                  userId: user.id,
                                  connectionName: user.connection_display_name,
                                })
                              "
                            />
                            <span class="checkmark"></span>
                            <div class="label-text" [title]="user.name">
                              {{ user.name }}
                              <span class="connection-info"
                                >({{ user.connection_display_name }})</span
                              >
                            </div>
                          </label>
                        </div>
                      } @empty {
                        <div class="no-results">No results found!</div>
                      }
                      @if (hasMoreUsers) {
                        <div class="load-more-wrapper">
                          <button
                            type="button"
                            class="load-more-btn"
                            (click)="loadMoreUsers()"
                            [disabled]="isLoadingMore"
                          >
                            @if (isLoadingMore) {
                              <span>Loading...</span>
                            } @else {
                              <span>Load More</span>
                            }
                          </button>
                        </div>
                      }
                    } @else {
                      <div class="no-results">Loading ...</div>
                    }
                  </div>
                </div>
              </div>
            }
          </div>
        </div>
      </div>
      <!-- Close add-recipients-section -->

      <!-- Existing Shares Section -->
      @if (
        !isLoadingExistingShares &&
        (existingSharedConnections.length > 0 || existingSharedUsers.length > 0)
      ) {
        <div class="existing-shares-section">
          <h4 class="section-title">Currently Shared With</h4>

          @if (existingSharedConnections.length > 0) {
            <div class="existing-connections">
              <h5 class="subsection-title">CDFIs</h5>
              <div class="existing-items">
                @for (
                  connection of existingSharedConnections;
                  track connection.id
                ) {
                  <div
                    class="existing-item"
                    [class.marked-for-removal]="
                      isConnectionMarkedForRemoval(connection.id)
                    "
                  >
                    <div class="item-info">
                      <span
                        class="item-name"
                        [title]="
                          connection.display_name || connection.connection_name
                        "
                        >{{
                          connection.display_name || connection.connection_name
                        }}</span
                      >
                    </div>
                    <div class="item-actions">
                      @if (isConnectionMarkedForRemoval(connection.id)) {
                        <button
                          type="button"
                          class="restore-btn"
                          (click)="restoreExistingConnection(connection)"
                          title="Restore sharing"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="10"
                            height="10"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                          >
                            <path
                              d="M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z"
                            />
                          </svg>
                        </button>
                      } @else {
                        <button
                          type="button"
                          class="remove-btn"
                          (click)="removeExistingConnection(connection)"
                          title="Remove sharing"
                        >
                          <svg
                            width="12px"
                            height="12px"
                            fill="#dc3545"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 384 512"
                          >
                            <path
                              d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z"
                            />
                          </svg>
                        </button>
                      }
                    </div>
                  </div>
                }
              </div>
            </div>
          }

          @if (existingSharedUsers.length > 0) {
            <div class="existing-users">
              <h5 class="subsection-title">Individual Users</h5>
              <div class="existing-items">
                @for (user of existingSharedUsers; track user.id) {
                  <div
                    class="existing-item"
                    [class.marked-for-removal]="isUserMarkedForRemoval(user.id)"
                  >
                    <div class="item-info">
                      <span class="item-name" [title]="user.name">
                        {{ user.name }}
                        @if (user.group_name) {
                          <span class="connection-info"
                            >({{ user.group_name }})</span
                          >
                        }
                      </span>
                    </div>
                    <div class="item-actions">
                      @if (isUserMarkedForRemoval(user.id)) {
                        <button
                          type="button"
                          class="restore-btn"
                          (click)="restoreExistingUser(user)"
                          title="Restore sharing"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="10"
                            height="10"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                          >
                            <path
                              d="M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z"
                            />
                          </svg>
                        </button>
                      } @else {
                        <button
                          type="button"
                          class="remove-btn"
                          (click)="removeExistingUser(user)"
                          title="Remove sharing"
                        >
                          <svg
                            width="12px"
                            height="12px"
                            fill="#dc3545"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 384 512"
                          >
                            <path
                              d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z"
                            />
                          </svg>
                        </button>
                      }
                    </div>
                  </div>
                }
              </div>
            </div>
          }
        </div>
      }

      @if (isLoadingExistingShares) {
        <div class="loading-existing">
          <div class="loading-text">Loading existing shares...</div>
        </div>
      } @else if (
        existingSharedConnections.length === 0 &&
        existingSharedUsers.length === 0
      ) {
        <div class="existing-shares-section">
          <h4 class="section-title">Currently Shared With</h4>
          <div class="no-existing-shares">
            <p>This file is not currently shared with anyone.</p>
          </div>
        </div>
      }
    </div>
  </app-popup>

  @if (showExternalShareConfirmation) {
    <app-popup
      [heading]="'Share File'"
      [confirmText]="'Share'"
      [cancelText]="'Cancel'"
      (confirmClicked)="confirmShareOperation()"
      (cancelClicked)="cancelShareConfirmation()"
    >
      <div style="padding: 1rem; font-size: 14px">
        The share list contains people outside your organization, are you sure?
      </div>
    </app-popup>
  }
}
